import { computed, signal } from "@preact/signals-core";
import { ApiRequestError } from "../lib/api";
import { bindErrorText, bindShow, bindText, bindValue, html, list } from "../lib/bind";
import { bindNullableValue, bindNumberValue, bindSelect } from "../lib/formBind";
import { form } from "../lib/form";
import { formatMoney, parseMoneyInput } from "../lib/money";
import { currentScope } from "../lib/scope";
import { labelRow } from "../lib/table";
import { toast } from "../lib/toast";
import { breadcrumb } from "../stores/breadcrumb";
import { type VatTreatment, VAT_TREATMENT_LABELS } from "../services/vat";
import { reclassifyClientInvoices } from "../services/invoice";
import {
  type Client,
  type ClientInput,
  archiveClient,
  createClient,
  getClient,
  listClientsPaged,
  updateClient,
} from "../services/client";
import {
  type Project,
  type ProjectInput,
  type RateUnit,
  createProject,
  deactivateProject,
  listProjects,
  updateProject,
} from "../services/project";
import { session } from "../stores/session";

function emptyClientInput(): ClientInput {
  return {
    name: "",
    contact_name: null,
    email: null,
    cc_emails: [],
    address_line1: null,
    address_line2: null,
    address_city: null,
    address_postcode: null,
    address_country: null,
    phone: null,
    default_currency: "GBP",
    payment_terms_days: 30,
    default_vat_rate: null,
    default_vat_treatment: null,
    notes: null,
  };
}

function toClientInput(client: Client): ClientInput {
  return {
    name: client.name,
    contact_name: client.contact_name,
    email: client.email,
    cc_emails: client.cc_emails,
    address_line1: client.address_line1,
    address_line2: client.address_line2,
    address_city: client.address_city,
    address_postcode: client.address_postcode,
    address_country: client.address_country,
    phone: client.phone,
    default_currency: client.default_currency,
    payment_terms_days: client.payment_terms_days,
    default_vat_rate: client.default_vat_rate,
    default_vat_treatment: client.default_vat_treatment,
    notes: client.notes,
  };
}

export function clientsListView(): Node {
  const clients = signal<Client[]>([]);
  const page = signal(1);
  const total = signal(0);
  const loadError = signal("");
  const searchQuery = signal("");

  function reload(): void {
    if (!session.companyId.value) {
      loadError.value = "Select a company first.";
      return;
    }
    listClientsPaged({ q: searchQuery.value || undefined, page: page.value, page_size: 50 })
      .then((result) => {
        clients.value = result.items;
        total.value = result.total;
        loadError.value = "";
      })
      .catch((err: unknown) => {
        loadError.value = err instanceof ApiRequestError ? err.body.message : "Failed to load";
      });
  }

  reload();

  const fragment = html`<div class="view">
    <p class="form-error"></p>
    <div class="card">
      <div class="card-label-row">
        <a href="/clients/new" data-link class="btn btn-primary">Add client</a>
      </div>
      <div class="form-field">
        <input class="search-input" placeholder="Search clients…" />
      </div>
      <div class="data-table">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Payment terms</th>
            </tr>
          </thead>
          <tbody class="clients-body"></tbody>
        </table>
      </div>

      <div class="pager">
        <button class="btn pager-prev" type="button">Previous</button>
        <span class="pager-label"></span>
        <button class="btn pager-next" type="button">Next</button>
      </div>
    </div>
  </div>`;

  const errorEl = fragment.querySelector<HTMLParagraphElement>(".form-error");
  const table = fragment.querySelector<HTMLTableElement>(".data-table table");
  const tbody = fragment.querySelector<HTMLTableSectionElement>(".clients-body");
  const searchInput = fragment.querySelector<HTMLInputElement>(".search-input");
  const pagerEl = fragment.querySelector<HTMLElement>(".pager");
  const pagerPrevBtn = fragment.querySelector<HTMLButtonElement>(".pager-prev");
  const pagerNextBtn = fragment.querySelector<HTMLButtonElement>(".pager-next");
  const pagerLabelEl = fragment.querySelector<HTMLElement>(".pager-label");
  if (
    !errorEl ||
    !table ||
    !tbody ||
    !searchInput ||
    !pagerEl ||
    !pagerPrevBtn ||
    !pagerNextBtn ||
    !pagerLabelEl
  ) {
    throw new Error("clients list markup missing expected elements");
  }

  bindErrorText(errorEl, loadError);
  bindValue(searchInput, searchQuery);
  searchInput.addEventListener("input", () => {
    page.value = 1;
    reload();
  });

  const pageCount = computed(() => Math.max(1, Math.ceil(total.value / 50)));
  currentScope().effect(() => {
    pagerEl.style.display = pageCount.value > 1 ? "" : "none";
    pagerLabelEl.textContent = `Page ${String(page.value)} of ${String(pageCount.value)}`;
    pagerPrevBtn.disabled = page.value <= 1;
    pagerNextBtn.disabled = page.value >= pageCount.value;
  });
  pagerPrevBtn.addEventListener("click", () => {
    if (page.value > 1) {
      page.value -= 1;
      reload();
    }
  });
  pagerNextBtn.addEventListener("click", () => {
    if (page.value < pageCount.value) {
      page.value += 1;
      reload();
    }
  });

  list(
    tbody,
    clients,
    (c) => c.id,
    (c) => {
      const row = html`<tr>
        <td><a href="/clients/${c.id}" data-link>${c.name}</a></td>
        <td>${c.email ?? ""}</td>
        <td>${String(c.payment_terms_days)} days</td>
      </tr>`.firstElementChild;
      if (!row) throw new Error("client row template produced no element");
      return labelRow(row, table);
    },
  );

  return fragment;
}

export function clientDetailView(params: Record<string, string>): Node {
  const paramId = params.id ?? "";
  const isNew = paramId === "new";
  const clientId = signal(isNew ? "" : paramId);
  const savedAt = signal("");
  const errorMessage = signal("");

  breadcrumb.value = { label: "Clients", path: "/clients" };
  currentScope().cleanup(() => {
    breadcrumb.value = null;
  });

  const clientForm = form(emptyClientInput());

  if (!isNew) {
    getClient(paramId)
      .then((client) => {
        clientForm.reset(toClientInput(client));
        savedAt.value = client.updated_at;
      })
      .catch((err: unknown) => {
        errorMessage.value = err instanceof ApiRequestError ? err.body.message : "Failed to load";
      });
  }

  async function submit(event: Event): Promise<void> {
    event.preventDefault();
    errorMessage.value = "";
    try {
      if (isNew) {
        const created = await createClient(clientForm.values());
        toast.queueSuccess("Client created.");
        location.assign(`/clients/${created.id}`);
        return;
      }
      const updated = await updateClient(clientId.value, {
        ...clientForm.values(),
        updated_at: savedAt.value,
      });
      savedAt.value = updated.updated_at;
      toast.success("Client saved.");
    } catch (err) {
      if (err instanceof ApiRequestError && err.status === 412) {
        errorMessage.value = "This record changed elsewhere — reload and retry.";
      } else {
        errorMessage.value = err instanceof ApiRequestError ? err.body.message : "Save failed";
      }
      toast.error(errorMessage.value);
    }
  }

  async function archive(): Promise<void> {
    if (!window.confirm("Archive this client?")) return;
    try {
      await archiveClient(clientId.value);
      toast.queueSuccess("Client archived.");
      location.assign("/clients");
    } catch (err) {
      toast.error(err instanceof ApiRequestError ? err.body.message : "Archive failed");
    }
  }

  const fragment = html`<div class="view">
    <div class="page-header">
      <h1>${isNew ? "New client" : "Client"}</h1>
    </div>
    <p class="form-error"></p>

    <form class="form client-form" onsubmit="${submit}">
      <div class="form-row">
        <div class="form-field">
          <label for="cl-name">Name</label>
          <input id="cl-name" required />
        </div>
        <div class="form-field">
          <label for="cl-contact-name">Contact name</label>
          <input id="cl-contact-name" />
        </div>
        <div class="form-field">
          <label for="cl-email">Email</label>
          <input id="cl-email" type="email" />
        </div>
        <div class="form-field">
          <label for="cl-cc-emails">CC emails (comma-separated)</label>
          <input id="cl-cc-emails" />
        </div>
      </div>
      <div class="form-row">
        <div class="form-field">
          <label for="cl-address-line1">Address line 1</label>
          <input id="cl-address-line1" />
        </div>
        <div class="form-field">
          <label for="cl-address-line2">Address line 2</label>
          <input id="cl-address-line2" />
        </div>
        <div class="form-field">
          <label for="cl-address-city">City</label>
          <input id="cl-address-city" />
        </div>
        <div class="form-field">
          <label for="cl-address-postcode">Postcode</label>
          <input id="cl-address-postcode" />
        </div>
        <div class="form-field">
          <label for="cl-address-country">Country</label>
          <input id="cl-address-country" />
        </div>
      </div>
      <div class="form-row">
        <div class="form-field">
          <label for="cl-phone">Phone</label>
          <input id="cl-phone" />
        </div>
        <div class="form-field">
          <label for="cl-currency">Default currency</label>
          <input id="cl-currency" />
        </div>
        <div class="form-field">
          <label for="cl-terms">Payment terms (days)</label>
          <input id="cl-terms" type="number" min="0" />
        </div>
        <div class="form-field">
          <label for="cl-default-vat-rate">Default VAT rate (%)</label>
          <input id="cl-default-vat-rate" inputmode="decimal" />
          <p class="field-hint">Leave blank to use the company's default VAT rate.</p>
        </div>
        <div class="form-field">
          <label for="cl-default-vat-treatment">Default VAT treatment</label>
          <select id="cl-default-vat-treatment">
            <option value="">Infer from the VAT rate</option>
            <option value="standard">Standard</option>
            <option value="reduced">Reduced</option>
            <option value="zero_rated">Zero-rated</option>
            <option value="exempt">Exempt</option>
            <option value="outside_scope">Outside scope of UK VAT</option>
          </select>
          <p class="field-hint">
            Set this to <strong>Outside scope of UK VAT</strong> for a business customer
            outside the UK — those supplies don't count towards flat-rate turnover, unlike
            zero-rated and exempt ones, which do.
          </p>
        </div>
      </div>
      <div class="form-field">
        <label for="cl-notes">Notes</label>
        <textarea id="cl-notes" rows="3"></textarea>
      </div>
      <div class="form-actions">
        <button class="btn btn-primary" type="submit">Save</button>
        <button class="btn btn-danger archive-btn" type="button" onclick="${archive}">
          Archive
        </button>
      </div>
    </form>

    <div class="projects-section"></div>
  </div>`;

  const errorEl = fragment.querySelector<HTMLParagraphElement>(".form-error");
  const archiveBtn = fragment.querySelector<HTMLButtonElement>(".archive-btn");
  const nameInput = fragment.querySelector<HTMLInputElement>("#cl-name");
  const contactNameInput = fragment.querySelector<HTMLInputElement>("#cl-contact-name");
  const emailInput = fragment.querySelector<HTMLInputElement>("#cl-email");
  const ccEmailsInput = fragment.querySelector<HTMLInputElement>("#cl-cc-emails");
  const addressLine1Input = fragment.querySelector<HTMLInputElement>("#cl-address-line1");
  const addressLine2Input = fragment.querySelector<HTMLInputElement>("#cl-address-line2");
  const addressCityInput = fragment.querySelector<HTMLInputElement>("#cl-address-city");
  const addressPostcodeInput = fragment.querySelector<HTMLInputElement>("#cl-address-postcode");
  const addressCountryInput = fragment.querySelector<HTMLInputElement>("#cl-address-country");
  const phoneInput = fragment.querySelector<HTMLInputElement>("#cl-phone");
  const currencyInput = fragment.querySelector<HTMLInputElement>("#cl-currency");
  const termsInput = fragment.querySelector<HTMLInputElement>("#cl-terms");
  const defaultVatRateInput = fragment.querySelector<HTMLInputElement>("#cl-default-vat-rate");
  const defaultVatTreatmentSelect = fragment.querySelector<HTMLSelectElement>(
    "#cl-default-vat-treatment",
  );
  const notesInput = fragment.querySelector<HTMLTextAreaElement>("#cl-notes");
  const projectsSection = fragment.querySelector<HTMLDivElement>(".projects-section");

  if (
    !errorEl ||
    !nameInput ||
    !contactNameInput ||
    !emailInput ||
    !ccEmailsInput ||
    !addressLine1Input ||
    !addressLine2Input ||
    !addressCityInput ||
    !addressPostcodeInput ||
    !addressCountryInput ||
    !phoneInput ||
    !currencyInput ||
    !termsInput ||
    !defaultVatRateInput ||
    !defaultVatTreatmentSelect ||
    !notesInput ||
    !projectsSection
  ) {
    throw new Error("client detail markup missing expected elements");
  }
  if (isNew && archiveBtn) archiveBtn.style.display = "none";

  bindErrorText(errorEl, errorMessage);
  bindValue(nameInput, clientForm.fields.name);
  bindNullableValue(contactNameInput, clientForm.fields.contact_name);
  bindNullableValue(emailInput, clientForm.fields.email);
  currentScope().effect(() => {
    const joined = clientForm.fields.cc_emails.value.join(", ");
    if (document.activeElement !== ccEmailsInput && ccEmailsInput.value !== joined) {
      ccEmailsInput.value = joined;
    }
  });
  ccEmailsInput.addEventListener("input", () => {
    clientForm.fields.cc_emails.value = ccEmailsInput.value
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
  });
  bindNullableValue(addressLine1Input, clientForm.fields.address_line1);
  bindNullableValue(addressLine2Input, clientForm.fields.address_line2);
  bindNullableValue(addressCityInput, clientForm.fields.address_city);
  bindNullableValue(addressPostcodeInput, clientForm.fields.address_postcode);
  bindNullableValue(addressCountryInput, clientForm.fields.address_country);
  bindNullableValue(phoneInput, clientForm.fields.phone);
  bindValue(currencyInput, clientForm.fields.default_currency);
  bindNumberValue(termsInput, clientForm.fields.payment_terms_days);
  bindNullableValue(defaultVatRateInput, clientForm.fields.default_vat_rate);
  // "" in the select means "no default set" — distinct from any real treatment, so it
  // maps to null rather than being coerced into one.
  currentScope().effect(() => {
    defaultVatTreatmentSelect.value = clientForm.fields.default_vat_treatment.value ?? "";
  });
  defaultVatTreatmentSelect.addEventListener("change", () => {
    clientForm.fields.default_vat_treatment.value =
      (defaultVatTreatmentSelect.value as VatTreatment | "") || null;
  });
  bindNullableValue(notesInput, clientForm.fields.notes);

  if (isNew) {
    projectsSection.style.display = "none";
  } else {
    projectsSection.appendChild(buildReclassifyPanel(clientId.value));
    projectsSection.appendChild(buildProjectsPanel(clientId.value));
  }

  return fragment;
}

/** Bulk VAT reclassification (12-vat-treatment.md § Phase B). Lives on the client because
 * that's the unit the real correction works in — "everything for this customer is outside
 * the scope" — and doing it invoice by invoice isn't viable across years of history. */
function buildReclassifyPanel(clientId: string): Node {
  const busy = signal(false);
  const outcome = signal("");

  const fragment = html`<div class="card reclassify-card">
    <div class="card-label">Reclassify VAT treatment</div>
    <p>
      Applies a VAT treatment to every invoice for this client at once. Invoices in a
      locked VAT period, and any where the change would alter the VAT charged, are left
      alone and listed.
    </p>
    <div class="form-row">
      <div class="form-field">
        <label for="cl-reclassify-treatment">Treatment</label>
        <select id="cl-reclassify-treatment">
          <option value="outside_scope">Outside scope of UK VAT</option>
          <option value="zero_rated">Zero-rated</option>
          <option value="exempt">Exempt</option>
          <option value="standard">Standard</option>
          <option value="reduced">Reduced</option>
        </select>
      </div>
      <div class="form-field checkbox-field">
        <input id="cl-reclassify-default" type="checkbox" checked />
        <label for="cl-reclassify-default">Also set as this client's default</label>
      </div>
      <div class="form-actions">
        <button class="btn reclassify-all-btn" type="button">Reclassify all</button>
      </div>
    </div>
    <p class="field-hint reclassify-outcome"></p>
  </div>`;

  const select = fragment.querySelector<HTMLSelectElement>("#cl-reclassify-treatment");
  const defaultCheckbox = fragment.querySelector<HTMLInputElement>("#cl-reclassify-default");
  const button = fragment.querySelector<HTMLButtonElement>(".reclassify-all-btn");
  const outcomeEl = fragment.querySelector<HTMLElement>(".reclassify-outcome");
  if (!select || !defaultCheckbox || !button || !outcomeEl) {
    throw new Error("reclassify panel markup missing expected elements");
  }

  bindText(outcomeEl, outcome);
  currentScope().effect(() => {
    button.disabled = busy.value;
  });

  button.addEventListener("click", () => {
    const treatment = select.value as VatTreatment;
    if (
      !window.confirm(
        `Reclassify every invoice for this client as "${VAT_TREATMENT_LABELS[treatment]}"? ` +
          "This changes which supplies count towards flat-rate turnover, so your VAT " +
          "figures will move.",
      )
    ) {
      return;
    }
    busy.value = true;
    void reclassifyClientInvoices(clientId, treatment, defaultCheckbox.checked)
      .then((result) => {
        const parts = [`${String(result.reclassified)} reclassified`];
        if (result.already_set > 0) parts.push(`${String(result.already_set)} already set`);
        if (result.skipped_locked.length > 0) {
          parts.push(`skipped (VAT period locked): ${result.skipped_locked.join(", ")}`);
        }
        if (result.skipped_rate_mismatch.length > 0) {
          parts.push(
            `skipped (would change the VAT charged): ${result.skipped_rate_mismatch.join(", ")}`,
          );
        }
        outcome.value = parts.join(" — ");
        toast.success(`${String(result.reclassified)} invoice(s) reclassified.`);
      })
      .catch((err: unknown) => {
        toast.error(err instanceof ApiRequestError ? err.body.message : "Reclassify failed");
      })
      .finally(() => {
        busy.value = false;
      });
  });

  return fragment;
}

function emptyProjectInput(): ProjectInput {
  return {
    name: "",
    code: null,
    rate: "0.00",
    rate_unit: "day",
    currency: null,
    active: true,
    notes: null,
    // Timesheet conversion defaults (05 § Timesheets) — editable per-project via the
    // project detail view once it grows the conversion-settings fields (Phase 2b UI).
    hours_per_day: "8.00",
    days_per_week: "5.00",
    days_per_month: "20.00",
  };
}

function buildProjectsPanel(clientId: string): Node {
  const projects = signal<Project[]>([]);
  const loadError = signal("");
  const showNewForm = signal(false);
  const newProjectForm = form(emptyProjectInput());

  function reload(): void {
    listProjects(clientId)
      .then((result) => {
        projects.value = result;
      })
      .catch((err: unknown) => {
        loadError.value = err instanceof ApiRequestError ? err.body.message : "Failed to load";
      });
  }
  reload();

  async function submitNewProject(event: Event): Promise<void> {
    event.preventDefault();
    try {
      await createProject(clientId, newProjectForm.values());
      newProjectForm.reset(emptyProjectInput());
      showNewForm.value = false;
      reload();
      toast.success("Project added.");
    } catch (err) {
      loadError.value = err instanceof ApiRequestError ? err.body.message : "Create failed";
      toast.error(loadError.value);
    }
  }

  const fragment = html`<div class="card">
    <div class="page-header">
      <div class="card-label">Projects</div>
      <button class="btn new-project-btn" type="button">Add project</button>
    </div>
    <p class="form-error"></p>

    <form class="form new-project-form" onsubmit="${submitNewProject}">
      <div class="form-row">
        <div class="form-field">
          <label for="np-name">Name</label>
          <input id="np-name" required />
        </div>
        <div class="form-field">
          <label for="np-code">Code</label>
          <input id="np-code" />
        </div>
        <div class="form-field">
          <label for="np-rate">Rate</label>
          <input id="np-rate" inputmode="decimal" />
        </div>
        <div class="form-field">
          <label for="np-rate-unit">Per</label>
          <select id="np-rate-unit">
            <option value="hour">Hour</option>
            <option value="day">Day</option>
            <option value="fixed">Fixed</option>
            <option value="item">Item</option>
          </select>
        </div>
      </div>
      <div class="form-actions">
        <button class="btn btn-primary" type="submit">Save project</button>
      </div>
    </form>

    <div class="data-table projects-table">
      <table>
        <thead>
          <tr>
            <th>Name</th>
            <th>Code</th>
            <th>Rate</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody class="projects-body"></tbody>
      </table>
    </div>
  </div>`;

  const newProjectBtn = fragment.querySelector<HTMLButtonElement>(".new-project-btn");
  const newProjectFormEl = fragment.querySelector<HTMLFormElement>(".new-project-form");
  const errorEl = fragment.querySelector<HTMLParagraphElement>(".form-error");
  const nameInput = fragment.querySelector<HTMLInputElement>("#np-name");
  const codeInput = fragment.querySelector<HTMLInputElement>("#np-code");
  const rateInput = fragment.querySelector<HTMLInputElement>("#np-rate");
  const rateUnitSelect = fragment.querySelector<HTMLSelectElement>("#np-rate-unit");
  const table = fragment.querySelector<HTMLTableElement>(".projects-table table");
  const tbody = fragment.querySelector<HTMLTableSectionElement>(".projects-body");

  if (
    !newProjectBtn ||
    !newProjectFormEl ||
    !errorEl ||
    !nameInput ||
    !codeInput ||
    !rateInput ||
    !rateUnitSelect ||
    !table ||
    !tbody
  ) {
    throw new Error("projects panel markup missing expected elements");
  }

  newProjectFormEl.style.display = "none";
  newProjectBtn.addEventListener("click", () => {
    showNewForm.value = !showNewForm.value;
    newProjectFormEl.style.display = showNewForm.value ? "" : "none";
  });

  bindErrorText(errorEl, loadError);
  bindValue(nameInput, newProjectForm.fields.name);
  bindNullableValue(codeInput, newProjectForm.fields.code);
  bindValue(rateInput, newProjectForm.fields.rate, {
    format: (v) => v,
    parse: (v) => parseMoneyInput(v) ?? v,
  });
  bindSelect(rateUnitSelect, newProjectForm.fields.rate_unit);

  function replaceProject(fresh: Project): void {
    projects.value = projects.value.map((p) => (p.id === fresh.id ? fresh : p));
  }

  list(
    tbody,
    projects,
    (p) => p.id,
    (p) => labelRow(buildProjectRow(p, replaceProject), table),
  );

  return fragment;
}

function buildProjectRow(project: Project, onUpdated: (fresh: Project) => void): Element {
  const editing = signal(false);
  const notEditing = computed(() => !editing.value);

  const editForm = form({
    name: project.name,
    code: project.code,
    rate: project.rate,
    rate_unit: project.rate_unit,
  });

  function startEdit(): void {
    editForm.reset({
      name: project.name,
      code: project.code,
      rate: project.rate,
      rate_unit: project.rate_unit,
    });
    editing.value = true;
  }

  function cancelEdit(): void {
    editing.value = false;
  }

  async function saveEdit(): Promise<void> {
    // The Save button here is a plain <button>, not a <form> submit — the "Add
    // project" form's `required` on its name input never runs for this path, so it
    // needs its own explicit check (this is the only currently-required project field).
    if (!editForm.fields.name.value.trim()) {
      toast.error("Name is required.");
      return;
    }
    try {
      const updated = await updateProject(project.id, {
        name: editForm.fields.name.value,
        code: editForm.fields.code.value,
        rate: editForm.fields.rate.value,
        rate_unit: editForm.fields.rate_unit.value,
        currency: project.currency,
        active: project.active,
        notes: project.notes,
        hours_per_day: project.hours_per_day,
        days_per_week: project.days_per_week,
        days_per_month: project.days_per_month,
        updated_at: project.updated_at,
      });
      // list() re-renders this row from the fresh object (Phase 2a item 13) —
      // no hand-patched DOM, and the re-render leaves the row in display mode.
      onUpdated(updated);
      toast.success("Project updated.");
    } catch (err) {
      const message =
        err instanceof ApiRequestError && err.status === 412
          ? "This project changed elsewhere — reload and retry."
          : err instanceof ApiRequestError
            ? err.body.message
            : "Update failed";
      toast.error(message);
    }
  }

  async function toggleActive(): Promise<void> {
    try {
      if (project.active) {
        const updated = await deactivateProject(project.id);
        onUpdated(updated);
        toast.success("Project deactivated.");
      } else {
        const updated = await updateProject(project.id, {
          name: project.name,
          code: project.code,
          rate: project.rate,
          rate_unit: project.rate_unit,
          currency: project.currency,
          active: true,
          notes: project.notes,
          hours_per_day: project.hours_per_day,
          days_per_week: project.days_per_week,
          days_per_month: project.days_per_month,
          updated_at: project.updated_at,
        });
        onUpdated(updated);
        toast.success("Project reactivated.");
      }
    } catch (err) {
      toast.error(err instanceof ApiRequestError ? err.body.message : "Update failed");
    }
  }

  const row = html`<tr>
    <td>
      <span class="project-name-display">${project.name}</span>
      <input class="project-edit-name project-edit-field" />
    </td>
    <td>
      <span class="project-code-display">${project.code ?? "—"}</span>
      <input class="project-edit-code project-edit-field" placeholder="Code" />
    </td>
    <td>
      <span class="project-rate-display">${formatMoney(project.rate, project.currency ?? "GBP")} / ${rateUnitLabel(project.rate_unit)}</span>
      <div class="project-edit-fields project-edit-field">
        <input class="project-edit-rate" inputmode="decimal" />
        <select class="project-edit-rate-unit">
          <option value="hour">Hour</option>
          <option value="day">Day</option>
          <option value="fixed">Fixed</option>
          <option value="item">Item</option>
        </select>
      </div>
    </td>
    <td class="project-status-display">${project.active ? "Active" : "Inactive"}</td>
    <td class="project-actions">
      <a href="/projects/${project.id}" data-link class="btn">View</a>
      <button class="btn edit-project-btn" type="button">Edit</button>
      <button class="btn btn-primary save-project-btn" type="button">Save</button>
      <button class="btn cancel-project-btn" type="button">Cancel</button>
      <button class="btn toggle-active-btn" type="button">${
        project.active ? "Deactivate" : "Reactivate"
      }</button>
    </td>
  </tr>`.firstElementChild;
  if (!row) throw new Error("project row template produced no element");

  const nameDisplay = row.querySelector<HTMLElement>(".project-name-display");
  const codeDisplay = row.querySelector<HTMLElement>(".project-code-display");
  const rateDisplay = row.querySelector<HTMLElement>(".project-rate-display");
  const statusDisplay = row.querySelector<HTMLElement>(".project-status-display");
  const editFieldEls = row.querySelectorAll<HTMLElement>(".project-edit-field");
  const editNameInput = row.querySelector<HTMLInputElement>(".project-edit-name");
  const editCodeInput = row.querySelector<HTMLInputElement>(".project-edit-code");
  const editRateInput = row.querySelector<HTMLInputElement>(".project-edit-rate");
  const editRateUnitSelect = row.querySelector<HTMLSelectElement>(".project-edit-rate-unit");
  const editBtn = row.querySelector<HTMLButtonElement>(".edit-project-btn");
  const saveBtn = row.querySelector<HTMLButtonElement>(".save-project-btn");
  const cancelBtn = row.querySelector<HTMLButtonElement>(".cancel-project-btn");
  const toggleBtn = row.querySelector<HTMLButtonElement>(".toggle-active-btn");

  if (
    !nameDisplay ||
    !codeDisplay ||
    !rateDisplay ||
    !statusDisplay ||
    !editNameInput ||
    !editCodeInput ||
    !editRateInput ||
    !editRateUnitSelect ||
    !editBtn ||
    !saveBtn ||
    !cancelBtn ||
    !toggleBtn
  ) {
    throw new Error("project row markup missing expected elements");
  }

  bindValue(editNameInput, editForm.fields.name);
  bindNullableValue(editCodeInput, editForm.fields.code);
  bindValue(editRateInput, editForm.fields.rate, {
    format: (v) => v,
    parse: (v) => parseMoneyInput(v) ?? v,
  });
  bindSelect(editRateUnitSelect, editForm.fields.rate_unit);

  bindShow(nameDisplay, notEditing);
  bindShow(codeDisplay, notEditing);
  bindShow(rateDisplay, notEditing);
  for (const el of editFieldEls) bindShow(el, editing);
  bindShow(editBtn, notEditing);
  bindShow(toggleBtn, notEditing);
  bindShow(saveBtn, editing);
  bindShow(cancelBtn, editing);

  editBtn.addEventListener("click", startEdit);
  cancelBtn.addEventListener("click", cancelEdit);
  saveBtn.addEventListener("click", () => {
    void saveEdit();
  });
  toggleBtn.addEventListener("click", () => {
    void toggleActive();
  });

  return row;
}

function rateUnitLabel(unit: RateUnit): string {
  return { hour: "hour", day: "day", fixed: "fixed", item: "item" }[unit];
}
